// src/ventes/Avoirs.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Row, Col, Form, Button, Modal,
    Spinner, Tooltip, OverlayTrigger, Pagination
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Avoirs() {
    const navigate = useNavigate();
    const [avoirs, setAvoirs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedAvoir, setSelectedAvoir] = useState(null);
    const [selectedAvoirLignes, setSelectedAvoirLignes] = useState([]);
    const [loadingLignes, setLoadingLignes] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_facture', direction: 'desc' });
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const [taxes, setTaxes] = useState({
        tva_taux: 18, css_taux: 1, tva_active: true, css_active: true
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchAvoirs();
        fetchTaxes();
        fetchSocieteInfos();
        loadLogoBase64();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setSocieteInfos(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => {
                    console.warn("Impossible de charger le logo");
                    setLogoBase64('');
                    resolve('');
                };
                img.src = logo;
            } catch (error) {
                console.error("Erreur chargement logo:", error);
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setTaxes(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchAvoirs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?type=avoir`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setAvoirs(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvoirLignes = async (idAvoir) => {
        setLoadingLignes(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?id=${idAvoir}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setSelectedAvoirLignes(result.data?.lignes || []);
        } catch (err) {
            console.error("Erreur chargement lignes:", err);
            setSelectedAvoirLignes([]);
        } finally {
            setLoadingLignes(false);
        }
    };

    /* ============================================================
       FILTER / SORT / PAGINATION
       ============================================================ */
    const filteredAvoirs = useMemo(() => {
        return avoirs
            .filter(a =>
                a.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                a.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                a.notes?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_facture') {
                    return sortConfig.direction === 'asc'
                        ? new Date(a.date_facture) - new Date(b.date_facture)
                        : new Date(b.date_facture) - new Date(a.date_facture);
                }
                if (sortConfig.key === 'montant') {
                    const mA = a.net_a_payer || 0;
                    const mB = b.net_a_payer || 0;
                    return sortConfig.direction === 'asc' ? mA - mB : mB - mA;
                }
                return 0;
            });
    }, [avoirs, filterText, sortConfig]);

    const totalPages = Math.ceil(filteredAvoirs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAvoirs.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    /* ============================================================
       STATS
       ============================================================ */
    const totalAvoirs = filteredAvoirs.reduce((acc, a) => {
        if (typeof a.net_a_payer === 'number') return acc + a.net_a_payer;
        const montant = parseFloat(String(a.net_a_payer).replace(/[^\d.-]/g, '')) || 0;
        return acc + montant;
    }, 0);

    const nbAvoirs = filteredAvoirs.length;
    const moyenneParAvoir = nbAvoirs > 0 ? totalAvoirs / nbAvoirs : 0;
    const nbClientsConcernes = new Set(avoirs.map(a => a.id_client)).size;
    const totalTVA = avoirs.reduce((acc, a) => acc + (a.montant_tva || 0), 0);

    /* ============================================================
       IMPRESSION (conservée à l'identique)
       ============================================================ */
    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    const genererHTMLAvoir = (avoir, avoirLignes) => {
        // ... (CODE IDENTIQUE — non modifié)
        const totalHT = avoir.montant_ht || 0;
        const totalTVA = avoir.montant_tva || 0;
        const totalCSS = avoir.montant_css || 0;
        const totalTTC = avoir.net_a_payer || 0;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Avoir ${avoir.numero_facture}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #eef2f7; padding: 20px; color: #1e293b; font-size: 11px; }
                    .invoice-container { margin: 0 auto; background: white; border-radius: 12px; overflow: hidden; }
                    .invoice-header { background: white; padding: 5px; border-bottom: 1px solid #e2e8f0; }
                    .header-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
                    .logo-area { display: flex; align-items: center; gap: 12px; }
                    .logo-img { width: auto; height: 60px; }
                    .logo-fallback { width: 50px; height: 50px; background: linear-gradient(135deg, #f59e0b, #d97706); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: bold; color: white; }
                    .company-name { font-size: 16px; font-weight: 700; color: #1a3c5e; }
                    .invoice-title { text-align: right; }
                    .invoice-title h2 { font-size: 18px; font-weight: 700; color: #f59e0b; letter-spacing: 1px; margin-bottom: 4px; }
                    .invoice-number { font-size: 10px; color: #64748b; }
                    .status-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 9px; font-weight: 600; margin-top: 6px; background: #fef3c7; color: #92400e; }
                    .company-details { display: flex; justify-content: space-between; font-size: 9px; color: #64748b; padding: 5px; border-top: 1px solid #e2e8f0; }
                    .invoice-body { padding: 5px 0px; }
                    .client-card { background: #f8fafc; border-radius: 10px; padding: 15px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-start; border: 1px solid #e2e8f0; }
                    .info-label { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #f59e0b; margin-bottom: 6px; }
                    .client-name { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
                    .client-details { font-size: 10px; color: #475569; line-height: 1.4; }
                    .date-item { margin-bottom: 6px; }
                    .date-label { font-size: 8px; color: #64748b; }
                    .date-value { font-size: 11px; font-weight: 600; color: #0f172a; }
                    .notes-box { margin-top: 15px; padding: 10px; background: #fef3c7; border-left: 3px solid #f59e0b; border-radius: 6px; font-size: 9px; color: #92400e; }
                    .products-table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 10px; }
                    .products-table th { background: #f8fafc; padding: 8px 10px; text-align: left; font-weight: 600; color: #1e293b; border-bottom: 1.5px solid #e2e8f0; }
                    .products-table td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; color: #334155; }
                    .text-right { text-align: right; }
                    .text-center { text-align: center; }
                    .totals { display: flex; justify-content: flex-end; margin-top: 15px; }
                    .totals-card { width: 280px; background: #f8fafc; border-radius: 10px; padding: 15px; }
                    .total-line { display: flex; justify-content: space-between; padding: 5px 0; font-size: 10px; border-bottom: 1px dashed #e2e8f0; }
                    .total-line.highlight { background: #fef3c7; margin: 8px -15px; padding: 8px 15px; font-weight: 600; }
                    .total-line.final { background: #fef2f2; margin-top: 8px; padding: 8px 15px; border-radius: 8px; }
                    .signature-section { margin-top: 25px; padding-top: 15px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: flex-end; }
                    .signature-box { text-align: center; }
                    .signature-line { width: 150px; height: 1px; background: #1a3c5e; margin-bottom: 6px; }
                    .signature-label { font-size: 8px; color: #64748b; }
                    .invoice-footer { background: #f8fafc; padding: 12px 25px; text-align: center; font-size: 8px; color: #64748b; border-top: 1px solid #e2e8f0; }
                    @media print { body { background: white; padding: 0; } .invoice-container { box-shadow: none; border-radius: 0; } }
                </style>
            </head>
            <body>
                <div class="invoice-container">
                    <div class="invoice-header">
                        <div class="header-top">
                            <div class="logo-area">
                                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo-img" />` : `<div class="logo-fallback">${logoText}</div>`}
                            </div>
                            <div class="invoice-title">
                                <h2>AVOIR</h2>
                                <div class="invoice-number">N° ${avoir.numero_facture}</div>
                                <div class="status-badge">NOTE DE CRÉDIT</div>
                            </div>
                        </div>
                        <div class="company-details">
                            <table style="width: 100%; border: 0px;">
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px; width: 50%;"><strong>Tél:</strong> ${company.phone}</td>
                                    <td style="padding: 6px 8px; border: 0px; width: 50%;"><strong>Adresse:</strong> ${company.address}</td>
                                </tr>
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px;"><strong>Email:</strong> ${company.email}</td>
                                    <td style="padding: 6px 8px; border: 0px;"><strong>Site web:</strong> ${company.site_web}</td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    <div class="invoice-body">
                        <div class="client-card">
                            <div>
                                <div class="info-label">CLIENT</div>
                                <div class="client-name">${avoir.client_nom || 'Client non spécifié'}</div>
                                <div class="client-details">
                                    <div>Code: ${avoir.code_client || ''}</div>
                                    ${avoir.client_telephone ? `<div>📞 ${avoir.client_telephone}</div>` : ''}
                                    ${avoir.client_email ? `<div>✉ ${avoir.client_email}</div>` : ''}
                                </div>
                            </div>
                            <div class="dates">
                                <div class="date-item">
                                    <div class="date-label">Date d'émission</div>
                                    <div class="date-value">${formatDate(avoir.date_facture)}</div>
                                </div>
                            </div>
                        </div>
                        ${avoir.notes ? `<div class="notes-box"><strong>📝 Motif :</strong> ${avoir.notes}</div>` : ''}
                        <table class="products-table">
                            <thead>
                                <tr>
                                    <th>Réf.</th><th>Désignation</th><th class="text-center">Qté</th>
                                    <th class="text-right">Prix HT</th><th class="text-right">Total HT</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${avoirLignes && avoirLignes.length > 0 
                                    ? avoirLignes.map(ligne => `
                                        <tr>
                                            <td><span style="font-family: monospace;">${ligne.reference || ''}</span></td>
                                            <td>${ligne.designation || ''}</td>
                                            <td class="text-center">${ligne.quantite || 0}</td>
                                            <td class="text-right">${formatCurrency(ligne.prix_unitaire_ht || 0)}</td>
                                            <td class="text-right"><strong>${formatCurrency((ligne.quantite || 0) * (ligne.prix_unitaire_ht || 0))}</strong></td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" class="text-center">Aucun produit dans cet avoir</td></tr>'}
                            </tbody>
                        </table>
                        <div class="totals">
                            <div class="totals-card">
                                <div class="total-line"><span>Total HT</span><span><strong>${formatCurrency(totalHT)}</strong></span></div>
                                ${taxes.tva_active && totalTVA > 0 ? `<div class="total-line"><span>TVA (${taxes.tva_taux}%)</span><span>${formatCurrency(totalTVA)}</span></div>` : ''}
                                ${taxes.css_active && totalCSS > 0 ? `<div class="total-line"><span>CSS (${taxes.css_taux}%)</span><span>${formatCurrency(totalCSS)}</span></div>` : ''}
                                <div class="total-line highlight"><span><strong>TOTAL TTC</strong></span><span><strong style="color: #f59e0b;">-${formatCurrency(totalTTC)}</strong></span></div>
                            </div>
                        </div>
                        <div class="signature-section">
                            <div class="signature-box"><div class="signature-line"></div><div class="signature-label">Signature client</div></div>
                            <div class="signature-box"><div class="signature-line"></div><div class="signature-label">Signature et cachet</div></div>
                        </div>
                    </div>
                    <div class="invoice-footer">
                        <div>Note de crédit — Avoir à déduire sur les prochaines factures</div>
                        <div style="margin-top: 4px;">${company.name} • ${company.phone} • ${company.email}</div>
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const handlePrintAvoir = (avoir) => {
        const fetchAndPrint = async () => {
            let avoirLignes = [];
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(
                    `${API_BASE_URL}/factures.php?id=${avoir.id_facture}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (response.ok) {
                    const result = await response.json();
                    avoirLignes = result.data?.lignes || [];
                }
            } catch (err) {
                console.error("Erreur chargement lignes:", err);
            }
            const htmlContent = genererHTMLAvoir(avoir, avoirLignes);
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
            iframe.contentWindow.onload = () => {
                iframe.contentWindow.print();
                setTimeout(() => {
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                }, 1000);
            };
        };
        fetchAndPrint();
    };

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading && !avoirs.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des avoirs...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                <i className="ri-file-copy-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Gestion des avoirs</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> {filteredAvoirs.length} avoir(s)
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-money-euro-circle-line"></i> {formatCurrency(totalAvoirs)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchAvoirs} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/ventes/factures')}>
                                <i className="ri-arrow-left-line"></i> Retour aux factures
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-file-copy-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total avoirs</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(totalAvoirs)}</h3>
                                    <span className="kpi-unit-charte">{nbAvoirs} avoir(s)</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-bar-chart-2-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Moyenne par avoir</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(moyenneParAvoir)}</h3>
                                    <span className="kpi-unit-charte">montant moyen</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-group-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Clients concernés</span>
                                    <h3 className="kpi-value-charte">{nbClientsConcernes}</h3>
                                    <span className="kpi-unit-charte">différents clients</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-percent-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">TVA totale</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(totalTVA)}</h3>
                                    <span className="kpi-unit-charte">{taxes.tva_active ? taxes.tva_taux : 0}% moyen</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher avoir, client, motif..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="filter-select-charte"
                                    >
                                        <option value={5}>5 / page</option>
                                        <option value={10}>10 / page</option>
                                        <option value={25}>25 / page</option>
                                        <option value={50}>50 / page</option>
                                    </Form.Select>
                                </Col>
                                <Col md="5" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {filteredAvoirs.length} avoir(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-file-copy-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des avoirs</span>
                                    <span className="badge-header-light">{filteredAvoirs.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">N° Avoir</th>
                                            <SortableHeader field="date_facture">Date</SortableHeader>
                                            <th>Client</th>
                                            <th className="text-end">Montant HT</th>
                                            <th className="text-end">TVA</th>
                                            <th className="text-end">Total TTC</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((avoir) => (
                                                <tr key={avoir.id_facture}>
                                                    <td className="ps-4" style={{ maxWidth: '140px' }}>
                                                        <span
                                                            className="table-link-charte"
                                                            style={{ color: COLORS.warning, cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setSelectedAvoir(avoir);
                                                                fetchAvoirLignes(avoir.id_facture);
                                                                setShowModal(true);
                                                            }}
                                                        >
                                                            <i className="ri-file-copy-line me-1"></i>
                                                            {avoir.numero_facture}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted">
                                                        <i className="ri-calendar-line me-1"></i>
                                                        {formatDate(avoir.date_facture)}
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">{avoir.client_nom}</div>
                                                        <small className="text-muted">{avoir.code_client}</small>
                                                    </td>
                                                    <td className="text-end">{formatCurrency(avoir.montant_ht)}</td>
                                                    <td className="text-end" style={{ color: COLORS.warning }}>
                                                        {formatCurrency(avoir.montant_tva)}
                                                    </td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                        −{formatCurrency(avoir.net_a_payer)}
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => {
                                                                        setSelectedAvoir(avoir);
                                                                        fetchAvoirLignes(avoir.id_facture);
                                                                        setShowModal(true);
                                                                    }}
                                                                    style={{ '--btn-color': COLORS.primary }}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Imprimer</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => handlePrintAvoir(avoir)}
                                                                    style={{ '--btn-color': COLORS.warning }}
                                                                >
                                                                    <i className="ri-printer-line"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun avoir trouvé</h6>
                                                        <p className="text-muted small mb-0">
                                                            Aucun avoir n'a été émis pour le moment
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAvoirs.length)}</strong> sur <strong>{filteredAvoirs.length}</strong> avoir(s)
                            </div>
                            <Pagination className="pagination-charte mb-0">
                                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== MODAL DÉTAIL ===== */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-file-copy-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Détail de l'avoir</Modal.Title>
                            <p className="text-white-50 mb-0 small">Note de crédit</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedAvoir && (
                        <>
                            {/* Résumé */}
                            <div className="summary-card-charte mb-3">
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                    <div>
                                        <div className="fw-bold" style={{ color: COLORS.primaryDark, fontSize: '1.1rem' }}>
                                            {selectedAvoir.numero_facture}
                                        </div>
                                        <div className="text-muted small mt-1">
                                            <i className="ri-calendar-line me-1"></i>
                                            {formatDate(selectedAvoir.date_facture)}
                                        </div>
                                    </div>
                                    <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                        <i className="ri-file-copy-line"></i>Avoir
                                    </span>
                                </div>
                            </div>

                            {/* Client */}
                            <div className="section-title-charte">
                                <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                    <i className="ri-user-line"></i>
                                </span>
                                <h6>Client</h6>
                            </div>
                            <div className="mb-4">
                                <div className="fw-bold">{selectedAvoir.client_nom}</div>
                                <div className="text-muted small">Code: {selectedAvoir.code_client}</div>
                                {selectedAvoir.client_telephone && (
                                    <div className="text-muted small mt-1">
                                        <i className="ri-phone-line me-1"></i>
                                        {selectedAvoir.client_telephone}
                                    </div>
                                )}
                            </div>

                            {/* Motif */}
                            {selectedAvoir.notes && (
                                <div className="alert-warning-charte mb-4">
                                    <div className="d-flex align-items-start">
                                        <div className="alert-warning-icon-charte"><i className="ri-file-text-line"></i></div>
                                        <div className="ms-3">
                                            <strong>Motif</strong>
                                            <p className="mb-0 small mt-1">{selectedAvoir.notes}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Lignes */}
                            <div className="section-title-charte">
                                <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                    <i className="ri-list-check-line"></i>
                                </span>
                                <h6>Lignes de l'avoir</h6>
                            </div>
                            {loadingLignes ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" style={{ color: COLORS.warning }} />
                                </div>
                            ) : (
                                <div className="table-responsive mb-4">
                                    <Table className="align-middle mb-0 table-charte">
                                        <thead>
                                            <tr>
                                                <th className="ps-4">Référence</th>
                                                <th>Produit</th>
                                                <th className="text-center">Qté</th>
                                                <th className="text-end">Prix HT</th>
                                                <th className="text-end">Total HT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedAvoirLignes.length > 0 ? (
                                                selectedAvoirLignes.map((ligne, idx) => (
                                                    <tr key={idx}>
                                                        <td className="ps-4">
                                                            <span className="reference-badge-charte">{ligne.reference}</span>
                                                        </td>
                                                        <td className="fw-semibold">{ligne.designation}</td>
                                                        <td className="text-center">
                                                            <span className="quantity-badge-charte">{ligne.quantite}</span>
                                                        </td>
                                                        <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht)}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        <p className="text-muted mb-0">Aucune ligne disponible</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {/* Récapitulatif */}
                            <div className="section-title-charte">
                                <span className="section-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                    <i className="ri-calculator-line"></i>
                                </span>
                                <h6>Récapitulatif</h6>
                            </div>
                            <div className="summary-card-charte">
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">Total HT</span>
                                    <span className="fw-bold">{formatCurrency(selectedAvoir.montant_ht)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">TVA ({taxes.tva_active ? taxes.tva_taux : 0}%)</span>
                                    <span className="fw-bold" style={{ color: COLORS.warning }}>
                                        {formatCurrency(selectedAvoir.montant_tva)}
                                    </span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">CSS ({taxes.css_active ? taxes.css_taux : 0}%)</span>
                                    <span className="fw-bold" style={{ color: COLORS.info }}>
                                        {formatCurrency(selectedAvoir.montant_css)}
                                    </span>
                                </div>
                                <hr />
                                <div className="d-flex justify-content-between">
                                    <span className="fw-bold fs-5">Total TTC</span>
                                    <span className="fw-bold fs-5" style={{ color: COLORS.danger }}>
                                        −{formatCurrency(selectedAvoir.net_a_payer)}
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button className="btn-modern-outline" onClick={() => setShowModal(false)}>
                        Fermer
                    </Button>
                    <Button className="btn-modern-warning" onClick={() => handlePrintAvoir(selectedAvoir)}>
                        <i className="ri-printer-line me-2"></i> Imprimer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.85rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-link-charte {
                    color: var(--primary);
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .table-link-charte:hover { text-decoration: underline; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .quantity-badge-charte {
                    display: inline-flex; align-items: center; justify-content: center;
                    min-width: 45px; padding: 0.25rem 0.6rem;
                    background: #f1f5f9; border-radius: 12px;
                    font-weight: 600; font-size: 0.8rem; color: #475569;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .alert-warning-charte {
                    border: none; background: #fffbeb;
                    border-left: 4px solid var(--warning);
                    border-radius: 16px; padding: 1rem 1.5rem;
                }
                .alert-warning-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(245,158,11,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--warning); font-size: 1.5rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-warning:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
